// ─────────────────────────────────────────────────────────────────────────────
// src/constants/permissions.ts
//
// Single source of truth for:
//  • Permission codes   (MODULE_ACTION format — must match backend DB values)
//  • Role names         (system roles recognised across the entire app)
//  • Permission actions (allowed action enum from the backend Permission model)
//
// ⚠️  DO NOT inline these strings directly in components.
//     Import from here so any rename is a one-line change.
// ─────────────────────────────────────────────────────────────────────────────

// ── Roles ─────────────────────────────────────────────────────────────────────

export const ROLES = {
  SUPER_ADMIN:     'SUPER_ADMIN',
  TENANT_ADMIN:    'TENANT_ADMIN',
  HR_MANAGER:      'HR_MANAGER',
  HR_EXECUTIVE:    'HR_EXECUTIVE',
  MANAGER:         'MANAGER',
  PROJECT_MANAGER: 'PROJECT_MANAGER',
  EMPLOYEE:        'EMPLOYEE',
} as const;

export type RoleName = typeof ROLES[keyof typeof ROLES];

/**
 * Roles that have elevated / admin access to HR data.
 * Used in places like "show all employees" vs "show only my records".
 */
export const ADMIN_ROLES: RoleName[] = [
  ROLES.SUPER_ADMIN,
  ROLES.TENANT_ADMIN,
  ROLES.HR_MANAGER,
  ROLES.HR_EXECUTIVE,
];

// ── Permission actions ─────────────────────────────────────────────────────────
// Must stay in sync with the backend Permission model `isIn` validator:
// ['CREATE', 'READ', 'UPDATE', 'DELETE', 'EXPORT', 'IMPORT', 'APPROVE', 'REJECT']
//
// NOTE: The live list is fetched at runtime via GET /permissions/actions/list.
//       This constant is the fallback for when the DB has no rows yet (first run).

export const PERMISSION_ACTIONS = {
  CREATE:  'CREATE',
  READ:    'READ',
  UPDATE:  'UPDATE',
  DELETE:  'DELETE',
  EXPORT:  'EXPORT',
  IMPORT:  'IMPORT',
  APPROVE: 'APPROVE',
  REJECT:  'REJECT',
} as const;

export type PermissionAction = typeof PERMISSION_ACTIONS[keyof typeof PERMISSION_ACTIONS];

/** Standard data-operation actions — shown in the CRUD section of the permission form */
export const CRUD_ACTION_LIST: PermissionAction[] = [
  PERMISSION_ACTIONS.CREATE,
  PERMISSION_ACTIONS.READ,
  PERMISSION_ACTIONS.UPDATE,
  PERMISSION_ACTIONS.DELETE,
];

/** All actions in the canonical display order (CRUD first, workflow second) */
export const ALL_ACTION_LIST: PermissionAction[] = [
  ...CRUD_ACTION_LIST,
  PERMISSION_ACTIONS.EXPORT,
  PERMISSION_ACTIONS.IMPORT,
  PERMISSION_ACTIONS.APPROVE,
  PERMISSION_ACTIONS.REJECT,
];

// ── Permission codes ───────────────────────────────────────────────────────────
// Format: {MODULE}_{ACTION}  (both uppercase, underscore-separated)
// Sorted by module for easy scanning. Add new ones here when new modules ship.

export const PERMISSIONS = {

  // ── Attendance ──────────────────────────────────────────────────────────────
  ATTENDANCE_APPROVE: 'ATTENDANCE_APPROVE',
  ATTENDANCE_CREATE:  'ATTENDANCE_CREATE',
  ATTENDANCE_READ:    'ATTENDANCE_READ',
  ATTENDANCE_UPDATE:  'ATTENDANCE_UPDATE',
  ATTENDANCE_VIEW:    'ATTENDANCE_VIEW',

  // ── Audit ───────────────────────────────────────────────────────────────────
  AUDIT_READ:         'AUDIT_READ',

  // ── Assets ──────────────────────────────────────────────────────────────────
  ASSET_READ:         'ASSET_READ',
  ASSET_CREATE:       'ASSET_CREATE',
  ASSET_UPDATE:       'ASSET_UPDATE',
  ASSET_DELETE:       'ASSET_DELETE',

  // ── Department ──────────────────────────────────────────────────────────────
  DEPARTMENT_CREATE:  'DEPARTMENT_CREATE',
  DEPARTMENT_DELETE:  'DEPARTMENT_DELETE',
  DEPARTMENT_READ:    'DEPARTMENT_READ',
  DEPARTMENT_UPDATE:  'DEPARTMENT_UPDATE',

  // ── Designation ─────────────────────────────────────────────────────────────
  DESIGNATION_CREATE: 'DESIGNATION_CREATE',
  DESIGNATION_DELETE: 'DESIGNATION_DELETE',
  DESIGNATION_READ:   'DESIGNATION_READ',
  DESIGNATION_UPDATE: 'DESIGNATION_UPDATE',

  // ── Document ────────────────────────────────────────────────────────────────
  DOCUMENT_READ:      'DOCUMENT_READ',
  DOCUMENT_CREATE:    'DOCUMENT_CREATE',
  DOCUMENT_UPDATE:    'DOCUMENT_UPDATE',
  DOCUMENT_DELETE:    'DOCUMENT_DELETE',

  // ── Employee ────────────────────────────────────────────────────────────────
  EMPLOYEE_CREATE:    'EMPLOYEE_CREATE',
  EMPLOYEE_DELETE:    'EMPLOYEE_DELETE',
  EMPLOYEE_EXPORT:    'EMPLOYEE_EXPORT',
  EMPLOYEE_READ:      'EMPLOYEE_READ',
  EMPLOYEE_UPDATE:    'EMPLOYEE_UPDATE',

  // ── Finance ─────────────────────────────────────────────────────────────────
  FINANCE_READ:       'FINANCE_READ',
  FINANCE_EXPORT:     'FINANCE_EXPORT',

  // ── Inventory ───────────────────────────────────────────────────────────────
  INVENTORY_CREATE:   'INVENTORY_CREATE',
  INVENTORY_DELETE:   'INVENTORY_DELETE',
  INVENTORY_READ:     'INVENTORY_READ',
  INVENTORY_UPDATE:   'INVENTORY_UPDATE',

  // ── Leave ───────────────────────────────────────────────────────────────────
  LEAVE_APPROVE:      'LEAVE_APPROVE',
  LEAVE_CREATE:       'LEAVE_CREATE',
  LEAVE_DELETE:       'LEAVE_DELETE',
  LEAVE_READ:         'LEAVE_READ',
  LEAVE_REJECT:       'LEAVE_REJECT',
  LEAVE_UPDATE:       'LEAVE_UPDATE',

  // ── Payroll ─────────────────────────────────────────────────────────────────
  PAYROLL_APPROVE:    'PAYROLL_APPROVE',
  PAYROLL_CREATE:     'PAYROLL_CREATE',
  PAYROLL_DELETE:     'PAYROLL_DELETE',
  PAYROLL_EXPORT:     'PAYROLL_EXPORT',
  PAYROLL_READ:       'PAYROLL_READ',
  PAYROLL_UPDATE:     'PAYROLL_UPDATE',
  PAYROLL_VIEW:       'PAYROLL_VIEW',

  // ── Performance ─────────────────────────────────────────────────────────────
  PERFORMANCE_READ:   'PERFORMANCE_READ',
  PERFORMANCE_CREATE: 'PERFORMANCE_CREATE',
  PERFORMANCE_UPDATE: 'PERFORMANCE_UPDATE',

  // ── Permission (meta — managing permissions themselves) ──────────────────────
  PERMISSION_CREATE:  'PERMISSION_CREATE',
  PERMISSION_DELETE:  'PERMISSION_DELETE',
  PERMISSION_READ:    'PERMISSION_READ',
  PERMISSION_UPDATE:  'PERMISSION_UPDATE',

  // ── Project ─────────────────────────────────────────────────────────────────
  PROJECT_READ:       'PROJECT_READ',
  PROJECT_CREATE:     'PROJECT_CREATE',
  PROJECT_UPDATE:     'PROJECT_UPDATE',
  PROJECT_DELETE:     'PROJECT_DELETE',

  // ── Purchase ────────────────────────────────────────────────────────────────
  PURCHASE_APPROVE:   'PURCHASE_APPROVE',
  PURCHASE_CREATE:    'PURCHASE_CREATE',
  PURCHASE_DELETE:    'PURCHASE_DELETE',
  PURCHASE_READ:      'PURCHASE_READ',
  PURCHASE_REJECT:    'PURCHASE_REJECT',
  PURCHASE_UPDATE:    'PURCHASE_UPDATE',

  // ── Recruitment ─────────────────────────────────────────────────────────────
  RECRUITMENT_READ:   'RECRUITMENT_READ',
  RECRUITMENT_CREATE: 'RECRUITMENT_CREATE',
  RECRUITMENT_UPDATE: 'RECRUITMENT_UPDATE',

  // ── Reports ─────────────────────────────────────────────────────────────────
  REPORTS_EXPORT:     'REPORTS_EXPORT',
  REPORTS_READ:       'REPORTS_READ',

  // ── Role (meta — managing roles themselves) ──────────────────────────────────
  ROLE_CREATE:        'ROLE_CREATE',
  ROLE_DELETE:        'ROLE_DELETE',
  ROLE_READ:          'ROLE_READ',
  ROLE_UPDATE:        'ROLE_UPDATE',

  // ── Salary ──────────────────────────────────────────────────────────────────
  SALARY_CREATE:      'SALARY_CREATE',
  SALARY_DELETE:      'SALARY_DELETE',
  SALARY_EXPORT:      'SALARY_EXPORT',
  SALARY_READ:        'SALARY_READ',
  SALARY_UPDATE:      'SALARY_UPDATE',

  // ── Sales ───────────────────────────────────────────────────────────────────
  SALES_CREATE:       'SALES_CREATE',
  SALES_DELETE:       'SALES_DELETE',
  SALES_READ:         'SALES_READ',
  SALES_UPDATE:       'SALES_UPDATE',

  // ── Shift ───────────────────────────────────────────────────────────────────
  SHIFT_CREATE:       'SHIFT_CREATE',
  SHIFT_DELETE:       'SHIFT_DELETE',
  SHIFT_READ:         'SHIFT_READ',
  SHIFT_UPDATE:       'SHIFT_UPDATE',
  SHIFT_VIEW:         'SHIFT_VIEW',

  // ── Store ───────────────────────────────────────────────────────────────────
  STORE_READ:         'STORE_READ',
  STORE_CREATE:       'STORE_CREATE',
  STORE_UPDATE:       'STORE_UPDATE',

  // ── System / Settings ───────────────────────────────────────────────────────
  SETTINGS_MANAGE:    'SETTINGS_MANAGE',
  SYSTEM_READ:        'SYSTEM_READ',
  SYSTEM_UPDATE:      'SYSTEM_UPDATE',

  // ── Worker Type ─────────────────────────────────────────────────────────────
  WORKER_TYPE_CREATE: 'WORKER_TYPE_CREATE',
  WORKER_TYPE_DELETE: 'WORKER_TYPE_DELETE',
  WORKER_TYPE_READ:   'WORKER_TYPE_READ',
  WORKER_TYPE_UPDATE: 'WORKER_TYPE_UPDATE',

} as const;

export type PermissionCode = typeof PERMISSIONS[keyof typeof PERMISSIONS];
